import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { AlertTriangle, CalendarDays, ChevronRight, Clock, ExternalLink, MapPin, Search, TrainFront } from 'lucide-react';
import './HowToReach.css';
import './CjpProtest.css';

/* ─────────────────────────────────────────────────────────────────────────
   /cjp-protest-2-0-mumbai
   News-style explainer for the Cockroach Janta Party (CJP) "Protest 2.0" in
   Mumbai on 2 October 2026 (Gandhi Jayanti), plus live all-mode route links
   to Shivaji Park from key cities. Facts are from published reports; keep
   STATUS_UPDATED and the status copy current as the story moves.
   ───────────────────────────────────────────────────────────────────────── */

const PAGE_URL = 'https://routechef.com/cjp-protest-2-0-mumbai';
const STATUS_UPDATED = '29 September 2026';

// Destination: Shivaji Park, Dadar (raw coordinates, same shape as corridor pages)
const VENUE = { name: 'Shivaji Park, Dadar, Mumbai', lat: 19.0269, lng: 72.8382 };

const ORIGIN_GROUPS = [
    {
        region: 'Maharashtra',
        cities: [
            ['Pune', 18.5204, 73.8567],
            ['Nashik', 19.9975, 73.7898],
            ['Nagpur', 21.1458, 79.0882],
            ['Aurangabad', 19.8762, 75.3433],
            ['Kolhapur', 16.705, 74.2433],
            ['Solapur', 17.6599, 75.9064],
            ['Amravati', 20.9374, 77.7796],
        ],
    },
    {
        region: 'North India',
        cities: [
            ['Delhi', 28.6139, 77.209],
            ['Jaipur', 26.9124, 75.7873],
            ['Lucknow', 26.8467, 80.9462],
            ['Chandigarh', 30.7333, 76.7794],
            ['Varanasi', 25.3176, 82.9739],
            ['Dehradun', 30.3165, 78.0322],
            ['Amritsar', 31.634, 74.8723],
        ],
    },
    {
        region: 'West & Central India',
        cities: [
            ['Ahmedabad', 23.0225, 72.5714],
            ['Surat', 21.1702, 72.8311],
            ['Vadodara', 22.3072, 73.1812],
            ['Goa', 15.4909, 73.8278],
            ['Indore', 22.7196, 75.8577],
            ['Bhopal', 23.2599, 77.4126],
            ['Jabalpur', 23.1815, 79.9864],
            ['Raipur', 21.2514, 81.6296],
        ],
    },
    {
        region: 'South India',
        cities: [
            ['Bangalore', 12.9716, 77.5946],
            ['Hyderabad', 17.385, 78.4867],
            ['Chennai', 13.0827, 80.2707],
            ['Kochi', 9.9312, 76.2673],
            ['Trivandrum', 8.5241, 76.9366],
            ['Mangalore', 12.9141, 74.856],
            ['Visakhapatnam', 17.6868, 83.2185],
        ],
    },
    {
        region: 'East & Northeast India',
        cities: [
            ['Kolkata', 22.5726, 88.3639],
            ['Patna', 25.5941, 85.1376],
            ['Ranchi', 23.3441, 85.3096],
            ['Bhubaneswar', 20.2961, 85.8245],
            ['Guwahati', 26.1445, 91.7362],
        ],
    },
];

const TRAVEL_DATES = [
    { value: '2026-09-30', label: 'Wed, 30 Sep' },
    { value: '2026-10-01', label: 'Thu, 1 Oct' },
    { value: '2026-10-02', label: 'Fri, 2 Oct' },
];

const FAQS = [
    {
        q: 'Where is the CJP Protest 2.0 happening?',
        a: 'The Cockroach Janta Party called the protest at Shivaji Park in Dadar, Mumbai. On 28 September 2026 Mumbai Police denied permission for the ground, and CJP said it would go ahead with a "Jail Bharo Andolan" at Shivaji Park on 2 October. Check CJP\'s official channels and Mumbai Police advisories for the final location before you travel.',
    },
    {
        q: 'When is the CJP protest in Mumbai?',
        a: 'Friday, 2 October 2026, which is Gandhi Jayanti. CJP\'s letter to the police asked for 3 pm to 9 pm, and later reports put the Jail Bharo Andolan at 4 pm.',
    },
    {
        q: 'Did Mumbai Police give permission for the CJP protest at Shivaji Park?',
        a: 'No. Police denied permission on 28 September 2026, citing a 2013 Bombay High Court order that limits Shivaji Park to sports use, the absence of BMC permission, the area\'s silence-zone status, and traffic near major hospitals. Police said they would consider an application for another location in Mumbai.',
    },
    {
        q: 'What is the CJP Jail Bharo Andolan?',
        a: 'A "jail bharo" (fill the jails) protest is a form of civil disobedience in which participants court arrest in large numbers. CJP announced it after permission for Shivaji Park was denied. Courting arrest carries legal consequences, so understand them before taking part.',
    },
    {
        q: 'What is the Cockroach Janta Party (CJP)?',
        a: 'A satirical youth-led political movement founded on 16 May 2026 by Abhijeet Dipke, a day after the Chief Justice of India\'s remarks describing some unemployed youth as "cockroaches". It grew rapidly on social media and led the Jantar Mantar protests in Delhi earlier in 2026.',
    },
    {
        q: 'Why is it called CJP Protest 2.0?',
        a: 'Supporters and media coverage use "2.0" for the Mumbai protest because it follows CJP\'s earlier protest at Jantar Mantar in Delhi.',
    },
    {
        q: 'What are the demands of the CJP protest?',
        a: 'CJP is demanding the resignation of Chief Election Commissioner Gyanesh Kumar and action against him, citing reports that two election commissioners formally objected to poll panel decisions several times. It frames the protest as a pledge for democracy on Gandhi Jayanti.',
    },
    {
        q: 'Who is leading the CJP protest in Mumbai?',
        a: 'CJP founder Abhijeet Dipke announced the protest. Reports also name national organisation head Ajinkya Shinde, office-bearer Pranay Ahire and co-convenor Ashutosh Ranka. Activist Sonam Wangchuk has expressed support.',
    },
    {
        q: 'How do I reach Shivaji Park, Dadar?',
        a: 'Dadar station, on both the Central and Western suburban lines, is about 1 km from Shivaji Park. Mumbai airport (BOM) is about 12 km away. Long-distance trains arrive at Dadar, LTT, CSMT, Mumbai Central and Bandra Terminus. Use the live route links on this page to plan from your city.',
    },
];

const SOURCES = [
    { title: 'Mumbai Police deny CJP\'s October 2 protest at Shivaji Park', outlet: 'Free Press Journal', url: 'https://www.freepressjournal.in/mumbai/mumbai-police-deny-cjps-october-2-protest-at-shivaji-park-cite-ground-use-restrictions-missing-bmc-permission-traffic-concerns' },
    { title: 'CJP to hold "Jail Bharo Andolan" in Mumbai on Oct 2 after police deny permission', outlet: 'The Week', url: 'https://www.theweek.in/news/india/2026/09/28/cjp-to-hold-jail-bharo-andolan-in-mumbai-on-oct-2-after-police-deny-permission.html' },
    { title: 'CJP to start "jail bharo" stir on October 2 as permission denied', outlet: 'Scroll', url: 'https://scroll.in/latest/1096059/cjp-to-start-jail-bharo-stir-on-october-2-as-police-denies-permission-for-protest-against-cec' },
    { title: 'Cockroach Janata Party announces Oct 2 Shivaji Park protest', outlet: 'Free Press Journal', url: 'https://www.freepressjournal.in/mumbai/cockroach-janata-party-announces-oct-2-shivaji-park-protest-over-election-commission-concerns' },
    { title: 'Cockroach Janta Party', outlet: 'Wikipedia', url: 'https://en.wikipedia.org/wiki/Cockroach_Janta_Party' },
];

const liveSearchUrl = (fromName, fromLat, fromLng, date) => {
    const p = new URLSearchParams({
        from: fromName,
        scode: '',
        stype: 'google',
        to: VENUE.name,
        dcode: '',
        dtype: 'google',
        date,
        adults: 1,
        children: 0,
        infants: 0,
        seatClasses: 'Economy,3A',
        modes: 'All',
        nonstop: 0,
        slat: fromLat,
        slng: fromLng,
        dlat: VENUE.lat,
        dlng: VENUE.lng,
    });
    return `/search-form?${p.toString()}`;
};

const CjpProtestMumbai = () => {
    const [travelDate, setTravelDate] = useState('2026-10-01');

    const pageTitle = 'CJP Protest 2.0 Mumbai: Shivaji Park, 2 October (Gandhi Jayanti) & How to Reach | RouteChef';
    const metaDescription =
        'Cockroach Janta Party (CJP) Protest 2.0 in Mumbai on 2 October 2026, Gandhi Jayanti: venue, timing, police permission status, Jail Bharo Andolan, demands, and live train, bus and flight routes to Shivaji Park, Dadar from cities across India.';

    const originCount = useMemo(() => ORIGIN_GROUPS.reduce((n, g) => n + g.cities.length, 0), []);

    useEffect(() => {
        document.title = pageTitle;
        const ensureMeta = (attr, key, content) => {
            let el = document.head.querySelector(`meta[${attr}="${key}"]`);
            if (!el) {
                el = document.createElement('meta');
                el.setAttribute(attr, key);
                document.head.appendChild(el);
            }
            el.setAttribute('content', content);
        };
        ensureMeta('name', 'description', metaDescription);
        ensureMeta('property', 'og:title', pageTitle);
        ensureMeta('property', 'og:description', metaDescription);
        ensureMeta('property', 'og:type', 'article');
        ensureMeta('property', 'og:url', PAGE_URL);

        let canonical = document.head.querySelector('link[rel="canonical"]');
        const hadCanonical = !!canonical;
        const prevCanonical = canonical?.href;
        if (!canonical) {
            canonical = document.createElement('link');
            canonical.setAttribute('rel', 'canonical');
            document.head.appendChild(canonical);
        }
        canonical.href = PAGE_URL;

        const ld = document.createElement('script');
        ld.type = 'application/ld+json';
        ld.id = 'cjp-protest-jsonld';
        ld.text = JSON.stringify([
            {
                '@context': 'https://schema.org',
                '@type': 'Article',
                headline: 'CJP Protest 2.0 in Mumbai on 2 October: venue, permission status and how to reach Shivaji Park',
                description: metaDescription,
                datePublished: '2026-09-29',
                dateModified: '2026-09-29',
                mainEntityOfPage: PAGE_URL,
                publisher: { '@type': 'Organization', name: 'RouteChef', url: 'https://routechef.com/' },
                about: [
                    { '@type': 'Organization', name: 'Cockroach Janta Party', alternateName: ['CJP', 'Cockroach Janata Party'] },
                    { '@type': 'Place', name: 'Shivaji Park', address: 'Dadar, Mumbai, Maharashtra' },
                ],
            },
            {
                '@context': 'https://schema.org',
                '@type': 'FAQPage',
                mainEntity: FAQS.map((f) => ({
                    '@type': 'Question',
                    name: f.q,
                    acceptedAnswer: { '@type': 'Answer', text: f.a },
                })),
            },
            {
                '@context': 'https://schema.org',
                '@type': 'BreadcrumbList',
                itemListElement: [
                    { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://routechef.com/' },
                    { '@type': 'ListItem', position: 2, name: 'CJP Protest 2.0 Mumbai', item: PAGE_URL },
                ],
            },
        ]);
        document.head.appendChild(ld);

        return () => {
            document.getElementById('cjp-protest-jsonld')?.remove();
            if (!hadCanonical) canonical.remove();
            else if (prevCanonical) canonical.href = prevCanonical;
        };
    }, [pageTitle, metaDescription]);

    return (
        <div className="htr-page">
            {/* ── Hero ── */}
            <header className="htr-hero">
                <nav className="htr-breadcrumb" aria-label="Breadcrumb">
                    <Link to="/">Home</Link>
                    <ChevronRight size={14} />
                    <span className="htr-crumb-current">CJP Protest 2.0 Mumbai</span>
                </nav>
                <h1>CJP Protest 2.0 in Mumbai on 2 October: Shivaji Park, Gandhi Jayanti and How to Reach</h1>
                <p className="htr-subtitle">
                    Everything travellers need on the Cockroach Janta Party protest in Mumbai: where and when it is planned,
                    the police permission status, what CJP is demanding, and live routes to Shivaji Park, Dadar from across India.
                </p>

                <div className="cjp-facts" aria-label="Key facts">
                    <div className="cjp-fact">
                        <CalendarDays size={18} />
                        <div>
                            <span className="cjp-fact-label">Date</span>
                            <strong>Fri, 2 October 2026</strong>
                            <span className="cjp-fact-sub">Gandhi Jayanti</span>
                        </div>
                    </div>
                    <div className="cjp-fact">
                        <MapPin size={18} />
                        <div>
                            <span className="cjp-fact-label">Planned venue</span>
                            <strong>Shivaji Park, Dadar</strong>
                            <span className="cjp-fact-sub">Mumbai</span>
                        </div>
                    </div>
                    <div className="cjp-fact">
                        <Clock size={18} />
                        <div>
                            <span className="cjp-fact-label">Time</span>
                            <strong>3 pm onwards</strong>
                            <span className="cjp-fact-sub">Jail Bharo reported at 4 pm</span>
                        </div>
                    </div>
                    <div className="cjp-fact">
                        <TrainFront size={18} />
                        <div>
                            <span className="cjp-fact-label">Nearest station</span>
                            <strong>Dadar</strong>
                            <span className="cjp-fact-sub">~1 km, Central &amp; Western lines</span>
                        </div>
                    </div>
                </div>

                {/* Status box: the single most important thing for anyone travelling */}
                <div className="cjp-status" role="status">
                    <div className="cjp-status-head">
                        <AlertTriangle size={18} />
                        <span>Permission status, updated {STATUS_UPDATED}</span>
                    </div>
                    <p>
                        Mumbai Police <strong>denied permission</strong> for the protest at Shivaji Park on 28 September 2026.
                        CJP has said it will hold a <strong>"Jail Bharo Andolan"</strong> at Shivaji Park on 2 October instead.
                        Plans may change at short notice, so confirm the latest on CJP&apos;s official channels and Mumbai Police
                        advisories before you travel.
                    </p>
                </div>
            </header>

            <main className="htr-content">
                {/* ── Live routes ── */}
                <section className="htr-section" id="live-routes" aria-labelledby="routes-heading">
                    <h2 id="routes-heading">Live routes to Shivaji Park, Mumbai from {originCount} cities</h2>
                    <p className="htr-para">
                        Each link opens a live RouteChef search from your city straight to Shivaji Park, comparing trains,
                        flights, buses and mixed-mode combinations in one place. Gandhi Jayanti is a national holiday, so
                        trains and flights into Mumbai around 1 and 2 October are likely to fill up early.
                    </p>

                    <div className="cjp-date-picker" role="radiogroup" aria-label="Travel date">
                        <span className="cjp-date-label">Travelling on</span>
                        {TRAVEL_DATES.map((d) => (
                            <button
                                key={d.value}
                                type="button"
                                role="radio"
                                aria-checked={travelDate === d.value}
                                className={`htr-mode-tab${travelDate === d.value ? ' active' : ''}`}
                                onClick={() => setTravelDate(d.value)}
                            >
                                {d.label}
                            </button>
                        ))}
                    </div>

                    {ORIGIN_GROUPS.map((group) => (
                        <div key={group.region} className="cjp-region">
                            <h3>{group.region}</h3>
                            <div className="cjp-origin-grid">
                                {group.cities.map(([name, lat, lng]) => (
                                    <Link
                                        key={name}
                                        to={liveSearchUrl(name, lat, lng, travelDate)}
                                        className="cjp-origin-link"
                                        rel="nofollow"
                                    >
                                        <span>{name} <ChevronRight size={14} className="htr-index-arrow" /> Mumbai</span>
                                        <Search size={15} />
                                    </Link>
                                ))}
                            </div>
                        </div>
                    ))}
                </section>

                {/* ── What's happening ── */}
                <section className="htr-section" aria-labelledby="what-heading">
                    <h2 id="what-heading">What is CJP Protest 2.0?</h2>
                    <p className="htr-para">
                        The Cockroach Janta Party (CJP) has called a protest in Mumbai on 2 October 2026 against the functioning
                        of the Election Commission of India. Its central demand is the resignation of Chief Election Commissioner
                        Gyanesh Kumar, citing reports that two election commissioners formally objected to poll panel decisions
                        on several occasions. CJP had set a deadline of 26 September for the resignation before announcing
                        protests.
                    </p>
                    <p className="htr-para">
                        The Mumbai protest is widely referred to as &quot;CJP Protest 2.0&quot; because it follows the party&apos;s
                        earlier protest at Jantar Mantar in Delhi. CJP founder Abhijeet Dipke announced the plan; reports also
                        name national organisation head Ajinkya Shinde and co-convenor Ashutosh Ranka.
                    </p>
                </section>

                {/* ── Gandhi Jayanti ── */}
                <section className="htr-section" aria-labelledby="gandhi-heading">
                    <h2 id="gandhi-heading">Why 2 October? The Gandhi Jayanti connection</h2>
                    <p className="htr-para">
                        2 October is Gandhi Jayanti, the birth anniversary of Mahatma Gandhi and a national holiday. CJP has framed
                        the day as a pledge for democracy. Its fallback plan, a &quot;jail bharo&quot; (fill the jails) protest,
                        draws on a form of civil disobedience associated with the freedom movement, in which participants court
                        arrest in large numbers.
                    </p>
                </section>

                {/* ── Permission ── */}
                <section className="htr-section" aria-labelledby="permission-heading">
                    <h2 id="permission-heading">Why Mumbai Police denied permission at Shivaji Park</h2>
                    <p className="htr-para">
                        CJP wrote to the police on 26 September seeking permission for a protest from 3 pm to 9 pm, promising a
                        peaceful demonstration with limited use of sound systems. On 28 September, police turned it down, citing:
                    </p>
                    <ul className="cjp-list">
                        <li>A 2013 Bombay High Court order that restricts Shivaji Park to sports use</li>
                        <li>No permission from the BMC, as required under a 2016 state government resolution</li>
                        <li>The residential area&apos;s silence-zone status and noise concerns</li>
                        <li>Traffic congestion risk near major hospitals and emergency routes</li>
                    </ul>
                    <p className="htr-para">
                        Police said they would consider an application for another location in Mumbai. CJP pointed to other
                        political gatherings held at the ground and responded with the Jail Bharo Andolan call. Local residents
                        have also objected to protests at the park.
                    </p>
                </section>

                {/* ── Getting to the venue ── */}
                <section className="htr-section" aria-labelledby="venue-heading">
                    <h2 id="venue-heading">Getting to Shivaji Park once you are in Mumbai</h2>
                    <div className="htr-table-wrap">
                        <table className="htr-table">
                            <thead>
                                <tr>
                                    <th>Arrival point</th>
                                    <th>Type</th>
                                    <th>Distance to Shivaji Park</th>
                                    <th>Onward</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr><td>Dadar (DR)</td><td>Railway Station</td><td><strong>1 km</strong></td><td>10–15 min walk</td></tr>
                                <tr><td>Mumbai Central (MMCT)</td><td>Railway Station</td><td><strong>6 km</strong></td><td>Western line local to Dadar</td></tr>
                                <tr><td>Bandra Terminus (BDTS)</td><td>Railway Station</td><td><strong>8 km</strong></td><td>Western line local or cab</td></tr>
                                <tr><td>Lokmanya Tilak Terminus (LTT)</td><td>Railway Station</td><td><strong>10 km</strong></td><td>Central line local via Kurla, or cab</td></tr>
                                <tr><td>CSMT</td><td>Railway Station</td><td><strong>11 km</strong></td><td>Central line local to Dadar</td></tr>
                                <tr><td>Mumbai Airport (BOM)</td><td>Airport</td><td><strong>12 km</strong></td><td>30–60 min by cab</td></tr>
                                <tr><td>Navi Mumbai Airport (NMI)</td><td>Airport</td><td><strong>35 km</strong></td><td>1–1.5 hrs by cab</td></tr>
                            </tbody>
                        </table>
                    </div>
                    <p className="htr-para cjp-note">
                        Expect traffic restrictions and heavy police deployment around Dadar on 2 October. Suburban trains to
                        Dadar are usually the quickest way in; allow extra time either way.
                    </p>
                </section>

                {/* ── FAQ ── */}
                <section className="htr-section" aria-labelledby="faq-heading">
                    <h2 id="faq-heading">CJP protest: frequently asked questions</h2>
                    <div className="htr-faqs">
                        {FAQS.map((f, i) => (
                            <details key={f.q} className="htr-faq" open={i === 0}>
                                <summary>{f.q}</summary>
                                <p>{f.a}</p>
                            </details>
                        ))}
                    </div>
                </section>

                {/* ── Sources ── */}
                <section className="htr-section" aria-labelledby="sources-heading">
                    <h2 id="sources-heading">Sources</h2>
                    <ul className="cjp-sources">
                        {SOURCES.map((s) => (
                            <li key={s.url}>
                                <a href={s.url} target="_blank" rel="noopener noreferrer">
                                    {s.title} <ExternalLink size={13} />
                                </a>
                                <span className="cjp-source-outlet">{s.outlet}</span>
                            </li>
                        ))}
                    </ul>
                </section>

                {/* ── Bottom CTA ── */}
                <section className="htr-final-cta">
                    <h2>Travelling to Mumbai for 2 October?</h2>
                    <p>Pick your city above, or search any route to Shivaji Park with live trains, buses and flights.</p>
                    <a href="#live-routes" className="htr-search-btn large">
                        <Search size={18} />
                        Find my route to Shivaji Park
                    </a>
                </section>

                <p className="htr-disclaimer">
                    RouteChef is a travel planning platform and is not affiliated with the Cockroach Janta Party or any
                    political organisation. This page summarises published news reports for travel planning; event details,
                    permissions and timings may change. Verify the latest information from official sources before travelling.
                    Last updated {STATUS_UPDATED}.
                </p>
            </main>
        </div>
    );
};

export default CjpProtestMumbai;
